import {createContext, useContext, useEffect, useRef, useState} from 'react'

import {useErrorHandler} from '../../../../../_metronic/helpers/ErrorHandler'
import * as ReportService from '../services/ReportService'

const PENDING_STATUSES = new Set([
  'billing_pending',
  'scheduled',
  'processing_files',
  'refund_pending',
  'calculating',
  'generating_map',
  'generating_pdf',
])

const EconomicReportContext = createContext()

export function useEconomicReportContext() {
  return useContext(EconomicReportContext)
}

export const EconomicReportConsumer = EconomicReportContext.Consumer

export function EconomicReportProvider({children}) {
  const {errorHandler} = useErrorHandler()
  const [loading, setLoading] = useState(true)
  const [reports, setReports] = useState([])
  const mountedRef = useRef(false)
  const pollTimeoutRef = useRef(null)

  const clearPollTimeout = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
  }

  const load = (silent = false) => {
    clearPollTimeout()

    if (!silent) {
      setLoading(true)
    }

    errorHandler(ReportService.listEconomicReports(0, 20))
      .then((res) => {
        if (!mountedRef.current) {
          return
        }

        if (res.ok) {
          const nextReports = res.data.economic_reports || []
          setReports(nextReports)

          if (nextReports.some((report) => PENDING_STATUSES.has(report.status))) {
            pollTimeoutRef.current = setTimeout(() => load(true), 5000)
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (mountedRef.current) {
          setLoading(false)
        }
      })
  }

  useEffect(() => {
    mountedRef.current = true
    load()

    return () => {
      mountedRef.current = false
      clearPollTimeout()
    }
    // Load once on mount; polling is managed by load itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = {
    reports,
    loading,
    load,
  }

  return <EconomicReportContext.Provider value={value}>{children}</EconomicReportContext.Provider>
}
